import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  DndContext, closestCenter, KeyboardSensor,
  PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../../ui/button';
import { ScrollArea } from '../../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import {
  LayoutTemplate, Type, Image as ImageIcon, MousePointerClick,
  Minus, Columns2, Plus, Eye, Code, Sparkles, Undo2
} from 'lucide-react';
import EmailBlockEditor from './EmailBlockEditor';
import { BLOCK_TYPES, PRESETS, createBlock, blocksToHtml } from './emailBuilderUtils';

const BLOCK_ICONS = { header: LayoutTemplate, text: Type, image: ImageIcon, button: MousePointerClick, separator: Minus, columns: Columns2 };

// ─── Sortable wrapper ───
const SortableBlock = ({ block, ...props }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style}>
      <EmailBlockEditor block={block} dragAttributes={attributes} dragListeners={listeners} {...props} />
    </div>
  );
};

const EmailBuilder = ({ blocks: initialBlocks, onChange, branding }) => {
  const [blocks, setBlocks] = useState(initialBlocks || []);
  const [editorMode, setEditorMode] = useState('visual'); // visual | preview | html
  const [rawHtml, setRawHtml] = useState('');

  // Sync with parent
  useEffect(() => {
    setBlocks(initialBlocks || []);
  }, [initialBlocks]);

  const htmlOutput = useMemo(() => blocksToHtml(blocks), [blocks]);

  useEffect(() => {
    if (editorMode === 'html') {
      setRawHtml(htmlOutput);
    }
  }, [editorMode, htmlOutput]);

  const updateBlocks = useCallback((newBlocks) => {
    setBlocks(newBlocks);
    onChange(newBlocks, blocksToHtml(newBlocks));
  }, [onChange]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = blocks.findIndex(b => b.id === active.id);
      const newIndex = blocks.findIndex(b => b.id === over.id);
      updateBlocks(arrayMove(blocks, oldIndex, newIndex));
    }
  }, [blocks, updateBlocks]);

  const addBlock = useCallback((type) => {
    const newBlock = createBlock(type);
    // Apply branding colors to header/button
    if (branding?.primary_color) {
      if (type === 'header') newBlock.data.bgColor = branding.primary_color;
      if (type === 'button') newBlock.data.bgColor = branding.primary_color;
    }
    if (branding?.logo_url && type === 'header') {
      newBlock.data.showLogo = true;
      newBlock.data.logoUrl = branding.logo_url;
    }
    updateBlocks([...blocks, newBlock]);
  }, [blocks, updateBlocks, branding]);

  const updateBlockData = useCallback((blockId, newData) => {
    updateBlocks(blocks.map(b => b.id === blockId ? { ...b, data: newData } : b));
  }, [blocks, updateBlocks]);

  const deleteBlock = useCallback((blockId) => {
    updateBlocks(blocks.filter(b => b.id !== blockId));
  }, [blocks, updateBlocks]);

  const moveBlock = useCallback((blockId, direction) => {
    const idx = blocks.findIndex(b => b.id === blockId);
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= blocks.length) return;
    updateBlocks(arrayMove(blocks, idx, newIdx));
  }, [blocks, updateBlocks]);

  const applyPreset = useCallback((preset) => {
    const newBlocks = preset.blocks.map(b => ({
      ...b,
      id: b.id + '-' + Date.now(),
      data: { ...b.data }
    }));
    // Apply branding to preset
    if (branding?.primary_color) {
      newBlocks.forEach(b => {
        if (b.type === 'header') b.data.bgColor = branding.primary_color;
        if (b.type === 'button') b.data.bgColor = branding.primary_color;
      });
    }
    if (branding?.logo_url) {
      newBlocks.forEach(b => {
        if (b.type === 'header') {
          b.data.showLogo = true;
          b.data.logoUrl = branding.logo_url;
        }
      });
    }
    updateBlocks(newBlocks);
  }, [updateBlocks, branding]);

  const handleHtmlSave = useCallback(() => {
    setEditorMode('visual');
    onChange(blocks, rawHtml);
  }, [blocks, rawHtml, onChange]);

  return (
    <div className="space-y-4" data-testid="email-builder">
      {/* Mode Tabs */}
      <Tabs value={editorMode} onValueChange={setEditorMode}>
        <div className="flex items-center justify-between">
          <TabsList className="h-8">
            <TabsTrigger value="visual" className="text-xs gap-1.5 h-7 px-3" data-testid="builder-mode-visual">
              <Sparkles className="w-3.5 h-3.5" />Builder
            </TabsTrigger>
            <TabsTrigger value="preview" className="text-xs gap-1.5 h-7 px-3" data-testid="builder-mode-preview">
              <Eye className="w-3.5 h-3.5" />Anteprima
            </TabsTrigger>
            <TabsTrigger value="html" className="text-xs gap-1.5 h-7 px-3" data-testid="builder-mode-html">
              <Code className="w-3.5 h-3.5" />HTML
            </TabsTrigger>
          </TabsList>
          {editorMode === 'visual' && blocks.length > 0 && (
            <Button variant="ghost" size="sm" className="text-xs h-7 text-muted-foreground" onClick={() => updateBlocks([])} data-testid="clear-blocks-btn">
              <Undo2 className="w-3 h-3 mr-1" />Svuota
            </Button>
          )}
        </div>

        {/* ─── VISUAL MODE ─── */}
        <TabsContent value="visual" className="mt-4 space-y-4">
          {blocks.length === 0 ? (
            <div className="space-y-4">
              {/* Empty state: show presets */}
              <div className="text-center py-6 border-2 border-dashed rounded-xl">
                <Sparkles className="w-8 h-8 text-primary/40 mx-auto mb-3" />
                <p className="font-medium text-sm mb-1">Inizia con un template</p>
                <p className="text-xs text-muted-foreground mb-4">Scegli un preset o aggiungi blocchi manualmente</p>
                <div className="grid grid-cols-3 gap-3 px-6">
                  {PRESETS.map((preset) => (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => applyPreset(preset)}
                      className="p-3 rounded-lg border text-left transition-all hover:border-primary hover:bg-primary/5"
                      data-testid={`preset-${preset.id}`}
                    >
                      <p className="text-sm font-medium">{preset.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{preset.preview}</p>
                    </button>
                  ))}
                </div>
              </div>
              {/* Block palette in empty state */}
              <div className="flex flex-wrap gap-2 justify-center">
                {BLOCK_TYPES.map(({ type, label }) => {
                  const Icon = BLOCK_ICONS[type];
                  return (
                    <Button key={type} variant="outline" size="sm" className="text-xs h-8 gap-1.5" onClick={() => addBlock(type)} data-testid={`add-block-${type}`}>
                      <Icon className="w-3.5 h-3.5" />{label}
                    </Button>
                  );
                })}
              </div>
            </div>
          ) : (
            <>
              {/* Block List with DnD */}
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={blocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-3">
                    {blocks.map((block, idx) => (
                      <SortableBlock
                        key={block.id}
                        block={block}
                        onChange={updateBlockData}
                        onDelete={() => deleteBlock(block.id)}
                        onMoveUp={() => moveBlock(block.id, -1)}
                        onMoveDown={() => moveBlock(block.id, 1)}
                        isFirst={idx === 0}
                        isLast={idx === blocks.length - 1}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              {/* Add block buttons */}
              <div className="flex flex-wrap gap-2 justify-center pt-2 border-t border-dashed">
                <span className="text-xs text-muted-foreground self-center mr-1">
                  <Plus className="w-3 h-3 inline" /> Aggiungi:
                </span>
                {BLOCK_TYPES.map(({ type, label }) => {
                  const Icon = BLOCK_ICONS[type];
                  return (
                    <Button key={type} variant="outline" size="sm" className="text-xs h-7 gap-1" onClick={() => addBlock(type)} data-testid={`add-block-${type}`}>
                      <Icon className="w-3 h-3" />{label}
                    </Button>
                  );
                })}
              </div>
            </>
          )}
        </TabsContent>

        {/* ─── PREVIEW MODE ─── */}
        <TabsContent value="preview" className="mt-4">
          <div className="border rounded-xl bg-neutral-100 p-6 flex justify-center" data-testid="email-preview-container">
            <div className="w-full max-w-[620px] bg-white rounded-xl shadow-sm overflow-hidden">
              {blocks.length > 0 ? (
                <div dangerouslySetInnerHTML={{ __html: htmlOutput }} />
              ) : (
                <div className="py-16 text-center text-muted-foreground text-sm">
                  Nessun contenuto. Torna al Builder per aggiungere blocchi.
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ─── HTML MODE ─── */}
        <TabsContent value="html" className="mt-4 space-y-3">
          <textarea
            value={rawHtml}
            onChange={(e) => setRawHtml(e.target.value)}
            className="w-full h-[300px] font-mono text-xs p-3 border rounded-lg bg-muted/30 resize-y focus:outline-none focus:ring-1 focus:ring-primary"
            data-testid="html-raw-editor"
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">Modifica l'HTML direttamente. Le modifiche sovrascriveranno il builder visuale.</p>
            <Button size="sm" variant="outline" className="text-xs h-7" onClick={handleHtmlSave} data-testid="html-apply-btn">
              Applica HTML
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EmailBuilder;
